<template>
  <button
    :type="type"
    :class="['zg-button', {
      [`zg-button--size-${size}`]: size,
      'zg-button--with-icons': icons.left || icons.right,
      'zg-button--round': round,
      'zg-button--outline': outline,
      'zg-button--link': link && (icons.left || icons.right),
      'zg-button--ghost': ghost,
      'zg-button--disabled': disabled || loading,
      'zg-button--full-width': fullWidth,
      'zg-button--full-width-mobile': fullWidthMobile,
      'zg-button--not-allowed': notAllowed
    }]"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <zg-loader
      v-if="loading"
      :size="size"
      button
    />

    <template v-else>
      <zg-icon
        v-if="icons && icons.left"
        :icon="icons.left"
        :rotate="icons.leftRotation"
      />

      <span>
        <slot />
      </span>

      <zg-icon
        v-if="icons && icons.right"
        :icon="icons.right"
        :rotate="icons.rightRotation"
      />
    </template>
  </button>
</template>

<script>
  import ZgIcon from '../ZgIcon/ZgIcon.vue'
  import ZgLoader from '../ZgLoader/ZgLoader.vue'

  export default {
    name: 'ZgButton',
    components: {
      ZgLoader,
      ZgIcon
    },
    props: {
      size: {
        type: String,
        required: false,
        default: 'large',
        validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value)
      },
      icons: {
        type: Object,
        required: false,
        default: () => ({
          left: null,
          leftRotation: 0,
          right: null,
          rightRotation: 0
        })
      },
      type: {
        type: String,
        required: false,
        default: undefined
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      round: {
        type: Boolean,
        required: false,
        default: false
      },
      outline: {
        type: Boolean,
        required: false,
        default: false
      },
      ghost: {
        type: Boolean,
        required: false,
        default: false
      },
      fullWidth: {
        type: Boolean,
        required: false,
        default: false
      },
      fullWidthMobile: {
        type: Boolean,
        required: false,
        default: false
      },
      loading: {
        type: Boolean,
        required: false,
        default: false
      },
      link: {
        type: Boolean,
        required: false,
        default: false
      },
      notAllowed: {
        type: Boolean,
        required: false,
        default: false
      }
    }
  }
</script>

<style lang="scss" scoped>
  $widths: (
    small: rem(100px),
    medium: rem(130px),
    large: rem(160px),
    xlarge: rem(180px),
  );

  $heights: (
    small: rem(32px),
    medium: rem(40px),
    large: rem(48px),
    xlarge: rem(64px),
  );

  $padding-x: (
    small: rem(12px),
    medium: rem(16px),
    large: rem(24px),
    xlarge: rem(32px),
  );

  $padding-y: (
    small: rem(6px),
    medium: rem(8px),
    large: rem(12px),
    xlarge: rem(18px),
  );

  .zg-button {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: auto;
    height: auto;
    background-color: color(primary-500);
    border: 2px solid color(primary-500);
    border-radius: rem(99px);
    cursor: pointer;
    outline: none;
    color: color(black);
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      background-color: color(brand-300);
      border-color: color(brand-300);
    }

    &:active {
      box-shadow: 0 0 0 4px color(brand-100);
    }

    &:focus {
      box-shadow: 0 0 0 4px color(input-shadow-focus);
    }

    &:focus:not(:focus-visible) {
      box-shadow: none;
    }

    &[disabled="disabled"] {
      @include disabled;
    }

    ::v-deep svg {
      color: color(black);
      fill: color(black)
    }
  }

  // ? ----------------------------------------------------------------------
  // size
  // ? ----------------------------------------------------------------------

  .zg-button--size-small {
    @include type-title-xxs;

    min-width: map-get($widths, small);
    height: map-get($heights, small);
    gap: rem(2px);
    padding: map-get($padding-y, small) map-get($padding-x, small);
    margin: 0;

    ::v-deep .zg-icon {
      width: rem(16px);
      height: rem(16px);
    }

    ::v-deep .zg-loader svg {
      width: rem(16px);
      height: rem(16px);
    }
  }

  .zg-button--size-medium {
    @include type-title-xs;

    min-width: map-get($widths, medium);
    height: map-get($heights, medium);
    gap: rem(4px);
    padding: map-get($padding-y, medium) map-get($padding-x, medium);
    margin: 0;

    ::v-deep .zg-icon {
      width: rem(20px);
      height: rem(20px);
    }

    ::v-deep .zg-loader svg {
      width: rem(20px);
      height: rem(20px);
    }
  }

  .zg-button--size-large {
    @include type-title-xs;

    min-width: map-get($widths, large);
    height: map-get($heights, large);
    gap: rem(6px);
    padding: map-get($padding-y, large) map-get($padding-x, large);
    margin: 0;

    ::v-deep .zg-icon {
      width: rem(24px);
      height: rem(24px);
    }

    ::v-deep .zg-loader svg {
      width: rem(24px);
      height: rem(24px);
    }
  }

  .zg-button--size-xlarge {
    @include type-title-s;

    min-width: map-get($widths, xlarge);
    height: map-get($heights, xlarge);
    gap: rem(6px);
    padding: map-get($padding-y, xlarge) map-get($padding-x, xlarge);
    margin: 0;

    ::v-deep .zg-icon {
      width: rem(24px);
      height: rem(24px);
    }

    ::v-deep .zg-loader svg {
      width: rem(24px);
      height: rem(24px);
    }
  }

  // ? ----------------------------------------------------------------------
  // icons
  // ? ----------------------------------------------------------------------

  .zg-button--with-icons {
    ::v-deep svg {
      flex-shrink: 0;
      height: auto;
      margin: 0;
    }
  }

  // ? ----------------------------------------------------------------------
  // round
  // ? ----------------------------------------------------------------------

  .zg-button--round {
    padding: 0;

    & > span {
      display: none;
    }

    &.zg-button--size-small {
      min-width: 0;
      width: map-get($heights, small);
      height: map-get($heights, small);
    }

    &.zg-button--size-medium {
      min-width: 0;
      width: map-get($heights, medium);
      height: map-get($heights, medium);
    }

    &.zg-button--size-large {
      min-width: 0;
      width: map-get($heights, large);
      height: map-get($heights, large);
    }

    &.zg-button--size-xlarge {
      min-width: 0;
      width: map-get($heights, xlarge);
      height: map-get($heights, xlarge);
    }
  }

  // ? ----------------------------------------------------------------------
  // outline
  // ? ----------------------------------------------------------------------

  .zg-button--outline {
    background-color: transparent;

    &:hover {
      background-color: transparent;
    }
  }

  // ? ----------------------------------------------------------------------
  // link
  // ? ----------------------------------------------------------------------

  .zg-button--link {
    background-color: transparent;
    border-color: transparent;
    padding: 0;
    width: auto;
    height: auto;
    min-width: auto;
    min-height: auto;
    border-radius: 0;

    &:hover {
      color: color(brand-500);
      background-color: transparent;
      border-color: transparent;

      ::v-deep svg {
        color: color(brand-500);
        fill: color(brand-500);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // ghost
  // ? ----------------------------------------------------------------------

  .zg-button--ghost {
    color: color(black);
    background-color: transparent;
    border-color: transparent;

    ::v-deep svg {
      color: color(black);
      fill: color(black);
    }
  }

  // ? ----------------------------------------------------------------------
  // disabled
  // ? ----------------------------------------------------------------------

  .zg-button--disabled {
    @include disabled;
  }

  // ? ----------------------------------------------------------------------
  // modifiers
  // ? ----------------------------------------------------------------------

  .zg-button--full-width {
    width: 100%;
  }

  .zg-button--full-width-mobile {
    @include mq($until: medium) {
      width: 100%;
    }
  }

  .zg-button--not-allowed {
    cursor: not-allowed;

    &:hover {
      cursor: not-allowed;
    }
  }
</style>
